/* Direcao visual PREMIUM ("Obsidian Aurora"), deliberadamente distinta das 128
   telas do prototipo: esta e pagina publica, nao tela de operacao. A paleta vive
   em packages/design-system/src/styles/premium.css e no AmbientBackground.
   Não aplicar aqui o retint enterprise. */
'use client';

import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '../../shared/auth/AuthContext';
import { ApiError } from '../../shared/api/client';
import { useNavigate } from '@tanstack/react-router';
import { AmbientBackground } from '../../shared/components/AmbientBackground';
import { Input, Button } from '@nexora/design-system';
import '../../../../../packages/design-system/src/styles/premium.css';

/* ─────────────────────────────────────────────
   Nexora — LoginPage Split Premium 55/45
   Obsidian Aurora · Sora headline · glass premium
   ───────────────────────────────────────────── */

/* ── Icons (stroke premium) ── */
const EyeIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);
const AtSignIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="4" />
    <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
  </svg>
);
const LockIcon = (): JSX.Element => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);
const ShieldIcon = (): JSX.Element => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
);
const ZapIcon = (): JSX.Element => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#06b6d4" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const LayersIcon = (): JSX.Element => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);
const AlertIcon = (): JSX.Element => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

/* ── Component ── */
export function LoginPage(): JSX.Element {
  const { login, isLoading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate({ to: '/dashboard', replace: true }).catch(console.error);
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Informe seu e-mail.');
      return;
    }
    if (!password) {
      setError('Informe sua senha.');
      return;
    }
    try {
      await login(email, password);
    } catch (err) {
      if (err instanceof ApiError) {
        const messages: Record<string, string> = {
          INVALID_CREDENTIALS: 'E-mail ou senha inválidos.',
          ACCOUNT_LOCKED: 'Conta temporariamente bloqueada por muitas tentativas. Tente novamente mais tarde.',
          SESSION_EXPIRED: 'Sessão expirada. Faça login novamente.',
          MFA_REQUIRED: 'Autenticação em duas etapas necessária.',
          MFA_VERIFICATION_FAILED: 'Código de verificação inválido.',
        };
        setError(messages[err.code] || err.message || 'Erro ao fazer login. Verifique suas credenciais.');
      } else {
        setError(err instanceof Error ? err.message : 'Erro ao fazer login. Verifique suas credenciais.');
      }
    }
  };

  return (
    <div className="nx-login">
      <AmbientBackground primaryColor="6, 182, 212" secondaryColor="14, 165, 233" />

      {/* ── Split container ── */}
      <div className="nx-login__split">
        {/* ═══ LEFT 55% — Storytelling premium ═══ */}
        <section className="nx-login__story" aria-label="Nexora plataforma">
          <div className="nx-login__story-inner">
            <div className="nx-login__brand">
              <span className="nx-login__brand-icon" aria-hidden="true">◆</span>
              <span className="nx-login__brand-name">Nexora</span>
              <span className="nx-login__brand-badge">ERP Premium</span>
            </div>

            <h1 className="nx-login__headline">
              A plataforma que
              <br />
              <span className="text-gradient-aurora">escala seu negócio</span>
            </h1>
            <p className="nx-login__sub">
              Gestão fiscal, financeira e operacional em um único lugar. Confiável como um banco, rápido como uma startup.
            </p>

            <ul className="nx-login__bullets" role="list">
              <li className="nx-login__bullet">
                <span className="nx-login__bullet-icon"><ShieldIcon /></span>
                <div>
                  <strong>Segurança</strong>
                  <span>Criptografia E2E, MFA, trilhas de auditoria e backup contínuo.</span>
                </div>
              </li>
              <li className="nx-login__bullet">
                <span className="nx-login__bullet-icon nx-login__bullet-icon--zap"><ZapIcon /></span>
                <div>
                  <strong>Performance</strong>
                  <span>Sub-120 ms p95, offline-first, PDV que nunca para.</span>
                </div>
              </li>
              <li className="nx-login__bullet">
                <span className="nx-login__bullet-icon nx-login__bullet-icon--gold"><LayersIcon /></span>
                <div>
                  <strong>Escalabilidade</strong>
                  <span>De 1 a 10 mil usuários, multi-filial e multi-tenant nativo.</span>
                </div>
              </li>
            </ul>

            <div className="nx-login__metrics" aria-label="Métricas">
              <div className="nx-login__metric">
                <span className="nx-login__metric-value"><span className="nx-login__counter" data-value="99.9">99,9</span>%</span>
                <span className="nx-login__metric-label">uptime SLA</span>
              </div>
              <div className="nx-login__metric-sep" aria-hidden="true" />
              <div className="nx-login__metric">
                <span className="nx-login__metric-value">+12k</span>
                <span className="nx-login__metric-label">empresas ativas</span>
              </div>
              <div className="nx-login__metric-sep" aria-hidden="true" />
              <div className="nx-login__metric">
                <span className="nx-login__metric-value">4,9/5</span>
                <span className="nx-login__metric-label">satisfação</span>
              </div>
            </div>

            <div className="nx-login__trust" aria-label="Certificações">
              <span className="nx-login__trust-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg>
                SOC 2 Tipo II
              </span>
              <span className="nx-login__trust-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                LGPD compliant
              </span>
              <span className="nx-login__trust-badge">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                Criptografia AES-256
              </span>
            </div>
          </div>
        </section>

        {/* ═══ RIGHT 45% — Form glass premium ═══ */}
        <section className="nx-login__panel" aria-label="Acessar plataforma">
          <div className="nx-login__card premium-card">
            <header className="nx-login__card-head">
              <p className="nx-login__eyebrow">Bem-vindo de volta</p>
              <h2 className="nx-login__title">Acessar plataforma</h2>
              <p className="nx-login__hint">Entre com seu e-mail corporativo e senha</p>
            </header>

            <form onSubmit={handleSubmit} className="nx-login__form" noValidate aria-describedby={error ? 'nx-login-error' : undefined}>
              <div className="nx-login__field">
                <Input
                  label="E-mail"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  iconLeft={<AtSignIcon />}
                  disabled={isLoading}
                  autoComplete="email"
                  autoFocus
                  inputSize="lg"
                  aria-required="true"
                />
              </div>

              <div className="nx-login__field nx-login__field--password">
                <Input
                  label="Senha"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  iconLeft={<LockIcon />}
                  disabled={isLoading}
                  autoComplete="current-password"
                  inputSize="lg"
                  aria-required="true"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="nx-login__eye"
                  aria-label={showPassword ? 'Esconder senha' : 'Mostrar senha'}
                  aria-pressed={showPassword}
                  tabIndex={0}
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>

              {error && (
                <div id="nx-login-error" className="nx-login__error" role="alert" aria-live="assertive">
                  <span className="nx-login__error-icon" aria-hidden="true"><AlertIcon /></span>
                  <span>{error}</span>
                </div>
              )}

              <Button
                variant="primary"
                size="lg"
                fullWidth
                type="submit"
                loading={isLoading}
                className="btn-shimmer nx-login__submit"
                aria-label={isLoading ? 'Entrando na plataforma' : 'Entrar na plataforma'}
              >
                {isLoading ? 'Entrando…' : 'Entrar'}
              </Button>

              <div className="nx-login__links">
                <a href="#" className="nx-login__link" onClick={(e: React.MouseEvent) => e.preventDefault()}>
                  Esqueci minha senha
                </a>
                <span className="nx-login__links-sep" aria-hidden="true">·</span>
                <a href="/register" className="nx-login__link">
                  Criar conta
                </a>
                <span className="nx-login__links-sep" aria-hidden="true">·</span>
                <a href="/" className="nx-login__link nx-login__link--muted">
                  Conheça a plataforma
                </a>
              </div>
            </form>

            <footer className="nx-login__footer">
              <span>© {new Date().getFullYear()} Nexora ERP · Todos os direitos reservados</span>
            </footer>
          </div>

          <p className="nx-login__legal">
            Ao entrar você concorda com nossos <a href="#" onClick={(e) => e.preventDefault()}>Termos</a> e <a href="#" onClick={(e) => e.preventDefault()}>Privacidade</a>.
          </p>
        </section>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap');

        .nx-login {
          position: relative;
          min-height: 100vh;
          min-height: 100dvh;
          background: radial-gradient(1200px 600px at 20% -10%, rgba(6,182,212,0.18), transparent 60%), radial-gradient(900px 500px at 90% 10%, rgba(245,158,11,0.10), transparent 60%), radial-gradient(800px 600px at 50% 120%, rgba(14,165,233,0.12), transparent 60%), #05070a;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
          display: flex;
          align-items: stretch;
          justify-content: center;
          overflow: hidden;
        }

        /* Split 55/45 */
        .nx-login__split {
          position: relative;
          z-index: 1;
          width: 100%;
          max-width: 1280px;
          display: flex;
          min-height: 100vh;
          min-height: 100dvh;
        }

        /* Storytelling 55% */
        .nx-login__story {
          flex: 1 1 55%;
          display: flex;
          align-items: center;
          padding: 3rem 3rem 3rem 2.5rem;
          color: #e2e8f0;
        }
        .nx-login__story-inner {
          max-width: 560px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 1.75rem;
          animation: nx-login-fade 560ms cubic-bezier(0.16,1,0.3,1) both;
        }
        .nx-login__brand {
          display: flex;
          align-items: center;
          gap: 0.65rem;
        }
        .nx-login__brand-icon {
          font-size: 1.6rem;
          color: #06b6d4;
          filter: drop-shadow(0 0 12px rgba(34,211,238,0.55));
          line-height: 1;
        }
        .nx-login__brand-name {
          font-family: 'Sora', sans-serif;
          font-weight: 800;
          font-size: 1.35rem;
          letter-spacing: -0.03em;
          color: #ffffff;
        }
        .nx-login__brand-badge {
          margin-left: 0.25rem;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #fde68a;
          background: rgba(245,158,11,0.14);
          border: 1px solid rgba(245,158,11,0.22);
          padding: 0.22rem 0.5rem;
          border-radius: 999px;
        }
        .nx-login__headline {
          font-family: 'Sora', sans-serif;
          font-weight: 800;
          font-size: clamp(1.85rem, 3.2vw, 2.75rem);
          line-height: 1.02;
          letter-spacing: -0.04em;
          color: #f8fafc;
          margin: 0;
        }
        .nx-login__sub {
          font-size: 1.02rem;
          line-height: 1.6;
          color: #94a3b8;
          margin: 0;
          max-width: 48ch;
        }
        .nx-login__bullets {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .nx-login__bullet {
          display: flex;
          gap: 0.9rem;
          align-items: flex-start;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.07);
          border-radius: 14px;
          padding: 0.9rem 1rem;
          backdrop-filter: blur(8px);
        }
        .nx-login__bullet-icon {
          width: 36px;
          height: 36px;
          flex-shrink: 0;
          display: grid;
          place-items: center;
          background: rgba(34,211,238,0.10);
          border: 1px solid rgba(34,211,238,0.18);
          border-radius: 10px;
        }
        .nx-login__bullet-icon--zap { background: rgba(56,189,248,0.10); border-color: rgba(56,189,248,0.18); }
        .nx-login__bullet-icon--gold { background: rgba(251,191,36,0.10); border-color: rgba(251,191,36,0.18); }
        .nx-login__bullet div { display: flex; flex-direction: column; gap: 0.15rem; }
        .nx-login__bullet strong {
          font-size: 0.92rem;
          font-weight: 700;
          color: #f8fafc;
          letter-spacing: -0.01em;
        }
        .nx-login__bullet span {
          font-size: 0.84rem;
          color: #94a3b8;
          line-height: 1.45;
        }
        .nx-login__metrics {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem 1.15rem;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 14px;
        }
        .nx-login__metric { display: flex; flex-direction: column; gap: 0.1rem; flex: 1; text-align: center; }
        .nx-login__metric-value {
          font-family: 'Sora', sans-serif;
          font-weight: 800;
          font-size: 1.15rem;
          color: #ffffff;
          letter-spacing: -0.03em;
        }
        .nx-login__metric-label { font-size: 0.72rem; color: #64748b; font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; }
        .nx-login__metric-sep { width: 1px; height: 28px; background: rgba(255,255,255,0.08); flex-shrink: 0; }
        .nx-login__trust {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }
        .nx-login__trust-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          font-size: 0.73rem;
          font-weight: 600;
          color: #cbd5e1;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          padding: 0.42rem 0.65rem;
          border-radius: 999px;
          letter-spacing: 0.02em;
        }

        /* Panel 45% */
        .nx-login__panel {
          flex: 1 1 45%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 2rem 2.5rem 2rem 1rem;
          gap: 0.85rem;
        }
        .nx-login__card {
          width: 100%;
          max-width: 420px;
          padding: 2rem 1.75rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          animation: nx-login-fade 560ms cubic-bezier(0.16,1,0.3,1) 120ms both;
        }
        .nx-login__card-head { display: flex; flex-direction: column; gap: 0.35rem; }
        .nx-login__eyebrow {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #06b6d4;
          margin: 0;
        }
        .nx-login__title {
          font-family: 'Sora', sans-serif;
          font-weight: 800;
          font-size: 1.45rem;
          letter-spacing: -0.03em;
          color: #f8fafc;
          margin: 0;
          line-height: 1.1;
        }
        .nx-login__hint { font-size: 0.875rem; color: #94a3b8; margin: 0; line-height: 1.5; }
        .nx-login__form { display: flex; flex-direction: column; gap: 1.15rem; }
        .nx-login__field { position: relative; }
        .nx-login__field--password .ds-input__container { padding-right: 2.4rem; }

        /* Input premium overrides — focus aurora */
        .nx-login__card .ds-input__label { color: #cbd5e1 !important; font-weight: 600 !important; font-size: 0.8125rem !important; }
        .nx-login__card .ds-input__container {
          background: rgba(255,255,255,0.05) !important;
          border-color: rgba(255,255,255,0.10) !important;
          border-radius: 12px !important;
          transition: border-color 200ms ease, box-shadow 200ms ease, background 200ms ease !important;
        }
        .nx-login__card .ds-input__container:hover { border-color: rgba(255,255,255,0.16) !important; }
        .nx-login__card .ds-input__container--focused {
          background: rgba(255,255,255,0.07) !important;
          border-color: rgba(6,182,212,0.55) !important;
          box-shadow: 0 0 0 3px rgba(6,182,212,0.16), 0 0 24px rgba(6,182,212,0.18) !important;
        }
        .nx-login__card .ds-input__container--error { border-color: rgba(239,68,68,0.65) !important; box-shadow: 0 0 0 3px rgba(239,68,68,0.14) !important; }
        .nx-login__card .ds-input__field { color: #f8fafc !important; }
        .nx-login__card .ds-input__field::placeholder { color: transparent !important; }
        .nx-login__card .ds-input__icon { color: rgba(255,255,255,0.42) !important; }
        .nx-login__card .ds-input__container--focused .ds-input__icon { color: #06b6d4 !important; }

        .nx-login__eye {
          position: absolute;
          right: 0.5rem;
          top: 50%;
          transform: translateY(-32%);
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          color: rgba(255,255,255,0.65);
          cursor: pointer;
          width: 32px;
          height: 32px;
          display: grid;
          place-items: center;
          border-radius: 8px;
          z-index: 2;
          transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
        }
        .nx-login__eye:hover { background: rgba(255,255,255,0.10); color: #ffffff; border-color: rgba(255,255,255,0.14); }
        .nx-login__eye:focus-visible { outline: 2px solid #06b6d4; outline-offset: 2px; }
        .nx-login__eye:disabled { opacity: 0.45; cursor: not-allowed; }

        /* Error premium */
        .nx-login__error {
          display: flex;
          align-items: center;
          gap: 0.55rem;
          padding: 0.75rem 0.85rem;
          border-radius: 12px;
          background: rgba(239,68,68,0.10);
          border: 1px solid rgba(239,68,68,0.22);
          color: #fca5a5;
          font-size: 0.84rem;
          line-height: 1.4;
          font-weight: 500;
        }
        .nx-login__error-icon { color: #f87171; display: inline-flex; flex-shrink: 0; }

        /* Submit shimmer + glow */
        .nx-login__submit {
          border-radius: 12px !important;
          font-weight: 700 !important;
          letter-spacing: -0.01em;
          background: linear-gradient(135deg, #06b6d4 0%, #0ea5e9 50%, #06b6d4 100%) !important;
          border-color: transparent !important;
          box-shadow: 0 8px 24px rgba(6,182,212,0.28), 0 2px 8px rgba(6,182,212,0.18) !important;
          transition: transform 180ms cubic-bezier(0.16,1,0.3,1), box-shadow 180ms ease !important;
        }
        .nx-login__submit:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 12px 32px rgba(6,182,212,0.34), 0 4px 12px rgba(6,182,212,0.22) !important; }
        .nx-login__submit:active:not(:disabled) { transform: translateY(0); }
        .nx-login__submit:focus-visible { box-shadow: 0 0 0 3px rgba(6,182,212,0.35), 0 8px 24px rgba(6,182,212,0.28) !important; }

        .nx-login__links {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.6rem;
          font-size: 0.84rem;
          flex-wrap: wrap;
        }
        .nx-login__link {
          color: #7dd3fc;
          text-decoration: none;
          font-weight: 600;
          transition: color 160ms ease;
        }
        .nx-login__link:hover { color: #bae6fd; text-decoration: underline; text-underline-offset: 3px; }
        .nx-login__link:focus-visible { outline: 2px solid #06b6d4; outline-offset: 2px; border-radius: 4px; }
        .nx-login__link--muted { color: #94a3b8; font-weight: 500; }
        .nx-login__link--muted:hover { color: #cbd5e1; }
        .nx-login__links-sep { color: #0f172a; }

        .nx-login__footer {
          display: flex;
          justify-content: center;
          border-top: 1px solid rgba(255,255,255,0.06);
          padding-top: 1rem;
          font-size: 0.72rem;
          color: #64748b;
          text-align: center;
        }
        .nx-login__legal {
          font-size: 0.72rem;
          color: #64748b;
          text-align: center;
          margin: 0;
          max-width: 420px;
        }
        .nx-login__legal a { color: #64748b; text-decoration: underline; text-underline-offset: 2px; }
        .nx-login__legal a:hover { color: #94a3b8; }
        .nx-login__legal a:focus-visible { outline: 2px solid #06b6d4; outline-offset: 2px; border-radius: 3px; }

        @keyframes nx-login-fade {
          from { opacity: 0; transform: translateY(14px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes nx-login-glow {
          0%,100% { opacity: 0.9; }
          50% { opacity: 1; }
        }

        /* Responsive: stack single coluna < 900px */
        @media (max-width: 900px) {
          .nx-login__split { flex-direction: column; }
          .nx-login__story { flex: none; padding: 1.75rem 1.25rem 0.5rem; }
          .nx-login__story-inner { max-width: none; }
          .nx-login__headline { font-size: 1.75rem; }
          .nx-login__metrics { padding: 0.85rem; }
          .nx-login__panel { flex: none; padding: 0.75rem 1.25rem 1.5rem; }
          .nx-login__card { max-width: 100%; }
        }
        @media (max-width: 520px) {
          .nx-login__bullets { gap: 0.75rem; }
          .nx-login__metric-value { font-size: 1rem; }
          .nx-login__trust-badge { font-size: 0.70rem; }
        }

        /* A11y reduced-motion */
        @media (prefers-reduced-motion: reduce) {
          .nx-login__story-inner, .nx-login__card { animation: none !important; }
          .nx-login__submit, .nx-login__eye, .nx-login__card .ds-input__container { transition: none !important; }
          .nx-login__submit:hover { transform: none !important; }
        }

        /* Focus aurora ring global */
        :where(.nx-login__card .ds-input__field):focus-visible { outline: none; }
      `}</style>
    </div>
  );
}
